import React from "react";
import { ScoreResult } from "../types";
import AdviceBlock from "./AdviceBlock";
import { Star, Landmark, AlertTriangle } from "lucide-react";

interface Props {
  data: ScoreResult;
  // «Подробнее у ассистента» в рекомендациях — без колбэка кнопка не показывается.
  onAskAssistant?: (question: string) => void;
}

export default function ScoreTab({ data, onAskAssistant }: Props) {
  // Translate class style
  const getClassStyle = (scoreClass: string) => {
    switch (scoreClass) {
      case "Класс 1":
        return { bg: "bg-emerald-50 border-emerald-200 text-emerald-800", rating: "Исключительная финансовая надежность", scoreColor: "text-emerald-600" };
      case "Класс 2":
        return { bg: "bg-teal-50 border-teal-200 text-teal-800", rating: "Высокая финансовая надежность", scoreColor: "text-teal-600" };
      case "Класс 3":
        return { bg: "bg-amber-50 border-amber-200 text-amber-800", rating: "Удовлетворительное состояние капитала", scoreColor: "text-amber-600" };
      case "Класс 4":
        return { bg: "bg-orange-50 border-orange-200 text-orange-900", rating: "Повышенный риск банкротства/неплатежей", scoreColor: "text-orange-600" };
      default:
        return { bg: "bg-rose-50 border-rose-250 text-rose-950", rating: "Критическая несостоятельность / Зона банкротства", scoreColor: "text-rose-600" };
    }
  };

  const currentStyle = getClassStyle(data.scoreClass);

  // Sberbank categories helper
  const getSberStyle = (sberClass: string) => {
    switch (sberClass) {
      case "Класс 1":
        return {
          icon: <Landmark className="w-5 h-5 text-emerald-600" />,
          title: "Первый класс (Заемщик высшей категории)",
          desc: "Кредитование осуществляется на общих/льготных условиях. Минимальный риск дефолта."
        };
      case "Класс 2":
        return {
          icon: <Landmark className="w-5 h-5 text-amber-600" />,
          title: "Второй класс (Средняя степень надежности)",
          desc: "Требует повышенного залогового обеспечения. Кредитование сопряжено с определенным уровнем риска."
        };
      default:
        return {
          icon: <AlertTriangle className="w-5 h-5 text-rose-600" />,
          title: "Третий класс (Проблемный заемщик)",
          desc: "Кредитование связано со значительным риском банкротства / неплатежеспособности."
        };
    }
  };

  const sber = getSberStyle(data.sberbankClass);

  const scoreUnits: Record<string, string> = {
    "Л2": "коэф.", "Л3": "коэф.", "Л4": "коэф.", "У12": "коэф.", "У1": "коэф.", "У24": "коэф.",
  };

  // Цвет мини-бара по доле набранных баллов от максимума строки — тот же порог
  // 0.8/0.5, что и в сводном баре методик (MethodologyView.toneFromRatio).
  const barColor = (points: number, maxPoints: number) => {
    const ratio = maxPoints ? points / maxPoints : 0;
    return ratio >= 0.8 ? "bg-emerald-500" : ratio >= 0.5 ? "bg-amber-500" : "bg-rose-500";
  };

  const scoreRows = [
    { code: "Л2", name: "Абсолютная ликвидность", ...data.details.L2 },
    { code: "Л3", name: "Быстрая ликвидность", ...data.details.L3 },
    { code: "Л4", name: "Текущая ликвидность", ...data.details.L4 },
    { code: "У12", name: "Собственные оборотные средства", ...data.details.U12 },
    { code: "У1", name: "Коэффициент автономии компании", ...data.details.U1 },
    { code: "У24", name: "Коэффициент маневренности капитала", ...data.details.U24 }
  ];

  return (
    <div id="score-tab-container" className="space-y-6">
      {/* 100-Points Indicator Header */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className={`lg:col-span-2 p-6 rounded-2xl border ${currentStyle.bg} flex flex-col justify-between space-y-4 shadow-sm`}>
          <div className="space-y-1">
            <div className="flex items-center gap-1.5 text-xs font-mono font-bold tracking-wider opacity-75 uppercase">
              <Star className="w-3.5 h-3.5 fill-current" />
              100-Балльная Экспресс-Оценка СЭБ Anyskin
            </div>
            <h3 className="text-xl font-bold font-display tracking-tight border-b border-black/5 pb-2">
              Присвоен: {data.scoreClass} — {currentStyle.rating}
            </h3>
            <p className="text-xs opacity-90 leading-relaxed pt-1">
              Балльная оценка ранжирует организацию в 5 классов надежности на основе взвешенного распределения баллов для ключевых 6 показателей ликвидности и финансовой независимости. Максимальное число баллов — 100.
            </p>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-4xl font-black font-display text-slate-900">{data.totalPoints} <span className="text-xs font-mono font-medium text-slate-500">баллов</span></span>
            <div className="flex-1">
              <div className="overflow-hidden h-2.5 text-xs flex rounded-full bg-black/10">
                <div 
                  style={{ width: `${data.totalPoints}%` }} 
                  className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-slate-900 transition-all duration-500"
                ></div>
              </div>
            </div>
          </div>
        </div>

        {/* TOP bank rating index */}
        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm space-y-4 flex flex-col justify-between font-sans">
          <div className="space-y-1">
            <div className="flex items-center gap-1.5 text-xs font-mono text-slate-400 font-bold tracking-wider uppercase">
              <Landmark className="w-3.5 h-3.5" />
              Методология TOP банка
            </div>
            <h4 className="font-bold text-slate-950 font-display text-base border-b border-slate-100 pb-2">
              Категория: {data.sberbankClass}
            </h4>
            <div className="flex items-start gap-2 pt-1 text-xs text-slate-700 leading-snug">
              {sber.icon}
              <div className="space-y-0.5">
                <span className="font-bold block text-slate-900">{sber.title}</span>
                <p className="text-slate-500">{sber.desc}</p>
              </div>
            </div>
          </div>
          <div className="pt-3 border-t border-slate-50 flex justify-between items-baseline font-mono">
            <span className="text-xs text-slate-400">Взвешенный балл TOP банка (S):</span>
            <span className="text-xl font-black text-slate-900">{data.details.sberbankDetails.weightedS} <span className="text-xs font-normal text-slate-500">баллов</span></span>
          </div>
        </div>
      </div>

      {/* 100pt scorecard Breakdown grid table */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden font-sans">
        <div className="p-5 border-b border-slate-50 bg-slate-50/50 flex items-center justify-between">
          <h4 className="font-bold font-display text-sm text-slate-900">Breakdown детального начисления баллов</h4>
          <span className="text-xs text-indigo-600 font-mono">Сумма: {data.totalPoints} / 100б</span>
        </div>
        <div className="divide-y divide-slate-100">
          {scoreRows.map((row) => (
            <div key={row.code} className="p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:bg-indigo-50/10 transition-colors">
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                    {row.code}
                  </span>
                  <span className="font-bold text-slate-900 font-display text-sm">{row.name}</span>
                </div>
                <div className="space-y-1 text-xs">
                  <p className="text-slate-450 font-mono">
                    Значение в балансе: <strong className="text-slate-800">{row.value}</strong> {scoreUnits[row.code] || "коэф."}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-6 self-end sm:self-center shrink-0">
                <div className="text-right">
                  <span className="text-slate-400 text-xs font-mono font-semibold">Начислено баллов:</span>
                  <div className="text-base font-black text-indigo-600 font-mono">
                    {row.points} <span className="text-xs font-normal text-slate-450">/ {row.maxPoints}</span>
                  </div>
                </div>
                {/* Micro progress-indicator bar */}
                <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden shrink-0">
                  <div
                    style={{ width: `${(row.points / row.maxPoints) * 100}%` }}
                    className={`h-full rounded-full transition-all duration-500 ${barColor(row.points, row.maxPoints)}`}
                  ></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <AdviceBlock conclusion={data.conclusion} recommendations={data.recommendations} onAskAssistant={onAskAssistant} />
    </div>
  );
}
